export type FeatureFlagType = 'release' | 'ops';

export type FeatureFlagTarget = {
  env?: 'development' | 'staging' | 'production';
  orgs?: string[];
  emails?: string[];
  groups?: string[];

  /** Email domains, matched exactly and case-insensitively; subdomains do not match. */
  domains?: string[];
};

export type FeatureFlagConfig = {
  type?: FeatureFlagType;
  owner?: string;
  reviewBy?: string;
  enabled?: boolean;
  targets?: FeatureFlagTarget[];
};

export type FeatureFlagContext = {
  env?: string;
  organisation?: string;
  groupId?: string | null;
  email?: string | null;
};

export type FeatureFlagValidationError = {
  message: string;
  target?: FeatureFlagTarget;
  index?: number;
};

export const isFeatureFlagConfig = (
  config: unknown
): config is FeatureFlagConfig => {
  if (!config || typeof config !== 'object') return false;

  return (
    'type' in config ||
    'owner' in config ||
    'reviewBy' in config ||
    'enabled' in config ||
    'targets' in config
  );
};

const normalizeEmail = (email?: string | null) => email?.trim().toLowerCase();

const normalizeDomain = (domain: string) =>
  domain.trim().toLowerCase().replace(/^@/, '');

const emailDomain = (email?: string) => {
  const at = email?.lastIndexOf('@') ?? -1;
  return at >= 0 ? email?.slice(at + 1) : undefined;
};

/** Validates a feature flag configuration. */
export const validateFeatureFlag = (
  config: FeatureFlagConfig
): FeatureFlagValidationError[] => {
  const errors: FeatureFlagValidationError[] = [];

  // Validate root fields
  if (config.type && !['release', 'ops'].includes(config.type)) {
    errors.push({
      message: `Invalid type: "${config.type}". Must be "release" or "ops".`,
    });
  }

  if (config.reviewBy) {
    const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
    if (!dateRegex.test(config.reviewBy)) {
      errors.push({
        message: `Invalid reviewBy date: "${config.reviewBy}". Must be in YYYY-MM-DD format.`,
      });
    }
  }

  if (
    typeof config.enabled !== 'undefined' &&
    typeof config.enabled !== 'boolean'
  ) {
    errors.push({
      message: `Invalid enabled field: must be a boolean, got ${typeof config.enabled}.`,
    });
  }

  // Validate targets array
  if (config.targets) {
    if (!Array.isArray(config.targets)) {
      errors.push({
        message: 'targets must be an array.',
      });
      return errors; // Can't continue validation if not an array
    }

    config.targets.forEach((target, index) => {
      if (!target || typeof target !== 'object') {
        errors.push({
          message: `Target at index ${index} must be an object.`,
          index,
        });
        return;
      }

      // Validate env
      if (
        target.env &&
        !['development', 'staging', 'production'].includes(target.env)
      ) {
        errors.push({
          message: `Invalid env in target at index ${index}: "${target.env}". Must be "development", "staging", or "production".`,
          target,
          index,
        });
      }

      // Validate orgs
      if (target.orgs) {
        if (!Array.isArray(target.orgs)) {
          errors.push({
            message: `orgs in target at index ${index} must be an array.`,
            target,
            index,
          });
        } else if (target.orgs.length === 0) {
          errors.push({
            message: `orgs array in target at index ${index} is empty. Remove the field or provide at least one org.`,
            target,
            index,
          });
        } else if (!target.orgs.every((org) => typeof org === 'string')) {
          errors.push({
            message: `All orgs in target at index ${index} must be strings.`,
            target,
            index,
          });
        }
      }

      // Validate emails
      if (target.emails) {
        if (!Array.isArray(target.emails)) {
          errors.push({
            message: `emails in target at index ${index} must be an array.`,
            target,
            index,
          });
        } else if (target.emails.length === 0) {
          errors.push({
            message: `emails array in target at index ${index} is empty. Remove the field or provide at least one email.`,
            target,
            index,
          });
        } else if (!target.emails.every((email) => typeof email === 'string')) {
          errors.push({
            message: `All emails in target at index ${index} must be strings.`,
            target,
            index,
          });
        }
      }

      // Validate groups
      if (target.groups) {
        if (!Array.isArray(target.groups)) {
          errors.push({
            message: `groups in target at index ${index} must be an array.`,
            target,
            index,
          });
        } else if (target.groups.length === 0) {
          errors.push({
            message: `groups array in target at index ${index} is empty. Remove the field or provide at least one group.`,
            target,
            index,
          });
        } else if (!target.groups.every((group) => typeof group === 'string')) {
          errors.push({
            message: `All groups in target at index ${index} must be strings.`,
            target,
            index,
          });
        }
      }

      // Validate domains
      if (target.domains) {
        if (!Array.isArray(target.domains)) {
          errors.push({
            message: `domains in target at index ${index} must be an array.`,
            target,
            index,
          });
        } else if (target.domains.length === 0) {
          errors.push({
            message: `domains array in target at index ${index} is empty. Remove the field or provide at least one domain.`,
            target,
            index,
          });
        } else if (
          !target.domains.every((domain) => typeof domain === 'string')
        ) {
          errors.push({
            message: `All domains in target at index ${index} must be strings.`,
            target,
            index,
          });
        }
      }

      // Warn if target has no conditions
      if (
        !target.env &&
        !target.orgs &&
        !target.emails &&
        !target.groups &&
        !target.domains
      ) {
        errors.push({
          message: `Target at index ${index} has no conditions. It will never match anything.`,
          target,
          index,
        });
      }
    });
  }

  return errors;
};

/**
 * Evaluates whether a feature flag is enabled based on the context. A feature
 * is enabled if at least one of its targets match, and a target matches if
 * ALL of its conditions are met.
 */
export const evaluateFeatureFlag = (
  config: FeatureFlagConfig,
  context: FeatureFlagContext
): boolean => {
  const targets = config.targets ?? [];

  // Check if ANY target matches
  for (const target of targets) {
    // Check env condition
    const envMatches = !target.env || target.env === context.env?.toLowerCase();

    // Check orgs condition
    const orgMatches =
      !target.orgs ||
      (context.organisation && target.orgs.includes(context.organisation));

    // Check emails condition (case-insensitive)
    const email = normalizeEmail(context.email);
    const emailMatches =
      !target.emails ||
      (email && target.emails.some((entry) => normalizeEmail(entry) === email));

    // Check groups condition
    const groupMatches =
      !target.groups ||
      (context.groupId && target.groups.includes(context.groupId));

    // Check domains condition (case-insensitive, exact domain)
    const domain = emailDomain(email);
    const domainMatches =
      !target.domains ||
      (domain &&
        target.domains.some((entry) => normalizeDomain(entry) === domain));

    // ALL conditions in this target must match
    if (
      envMatches &&
      orgMatches &&
      emailMatches &&
      groupMatches &&
      domainMatches
    ) {
      return true;
    }
  }

  // No targets matched, use fallback
  return config.enabled ?? false;
};
